import { trackVoiceWork } from "./voice-work-tracker";
/** Presentation steps are durable FIFO work, never latest-bubble-wins work. */
export class TtsPresentationQueue {
  private receipts = new Map<string, Promise<boolean>>();
  private pending = new Map<string, Set<Promise<boolean>>>();
  private generation = 0;
  private cancelledSources = new Set<string>();

  enqueue(
    executionId: string | undefined,
    source: string,
    run: (isCurrent: () => boolean) => Promise<boolean>,
    onDuplicate: () => void,
  ): void {
    const key = JSON.stringify([executionId, source]);
    if (this.cancelledSources.has(source) || this.receipts.has(key)) {
      onDuplicate();
      return;
    }
    const generation = this.generation;
    const id = executionId ?? "";
    const pending = this.pending.get(id) ?? new Set<Promise<boolean>>();
    this.pending.set(id, pending);
    // Register before invoking run: mirror relays can arrive while playback
    // is queued, and must share the same step receipt as the owner's request.
    const result = Promise.resolve()
      .then(() =>
        run(
          () =>
            generation === this.generation &&
            !this.cancelledSources.has(source),
        ),
      )
      .catch(() => false)
      .then((succeeded) => {
        pending.delete(result);
        if (this.pending.get(id) === pending && pending.size === 0) {
          this.pending.delete(id);
        }
        if (!succeeded && generation === this.generation) {
          this.receipts.delete(key);
        }
        this.pruneReceipts();
        return succeeded;
      });
    trackVoiceWork(result);
    pending.add(result);
    this.receipts.set(key, result);
  }

  wait(executionId: string): Promise<unknown> | undefined {
    const pending = this.pending.get(executionId);
    return pending?.size ? Promise.all(pending) : undefined;
  }

  hasPending(executionId: string): boolean {
    return Boolean(this.pending.get(executionId)?.size);
  }

  private pruneReceipts(): void {
    if (this.receipts.size <= 512) return;
    const pending = new Set(
      [...this.pending.values()].flatMap((items) => [...items]),
    );
    for (const [key, result] of this.receipts) {
      if (!pending.has(result)) this.receipts.delete(key);
      if (this.receipts.size <= 512) break;
    }
  }

  cancelSource(source: string): void {
    this.cancelledSources.add(source);
    if (this.cancelledSources.size > 512) {
      this.cancelledSources.delete(
        this.cancelledSources.values().next().value!,
      );
    }
  }

  cancel(): void {
    this.generation += 1;
    this.pending.clear();
  }

  reset(): void {
    this.cancel();
    this.receipts.clear();
    this.cancelledSources.clear();
  }
}
